import { supabase } from './supabase'

/**
 * En sanntidskanal ingen andre kan melde deg av.
 *
 * ## Fella
 *
 * `supabase.channel(topic)` LAGER IKKE en kanal — den gjenbruker en som
 * allerede finnes. Fra `@supabase/realtime-js` 2.98.0, `RealtimeClient.js:277`:
 *
 *     const exists = this.getChannels().find((c) => c.topic === realtimeTopic)
 *     if (!exists) { … } else { return exists }
 *
 * To komponenter som ber om samme emne får altså SAMME objekt. Begge legger på
 * sin `.on(...)`, begge kaller `.subscribe()` (den andre er en no-op, fordi
 * `subscribe` bare gjør noe når kanalen er `closed`) — og alt ser riktig ut.
 *
 * Helt til den ene avmonteres. Da kjører dens `useEffect`-opprydding
 * `supabase.removeChannel(channel)`, og den melder av kanalen **for begge**.
 * Den som ble stående igjen får aldri en hendelse mer, og har ingen måte å vite
 * det på: ingen feil, ingen logglinje, bare et tall som slutter å oppdatere seg.
 *
 * ## Slik traff den oss
 *
 * `useUnreadCount` kjørte i BÅDE sidemenyen og bunnmenyen, med emnet
 * `unread-count-<uid>`. Bunnmenyen avmonteres når du går inn i et arrangement
 * (`AppLayout`: `{!insideEvent && <BottomNav />}`) — og fra det øyeblikket sto
 * sidemenyens varselteller stille resten av økta. Nøyaktig samme form hadde
 * `inbox-chats` (sidemeny + bunnmeny + /messages) og `notifications-<uid>`
 * (varselbjella i toppen + /notifications).
 *
 * ## Regelen
 *
 * Et abonnement eier sin egen kanal. Suffikset gjør emnet unikt per kall, så
 * `removeChannel` aldri kan nå lenger enn til den som ba om den. Emnet du
 * sender inn beholdes som prefiks, så navnet fortsatt sier hva kanalen er til —
 * det er bare ikke lenger et delt navnerom.
 *
 * `scripts/check-realtime-topic.mjs` holder linja: `.channel(` direkte på
 * klienten feiler bygget.
 */
let subscriberSeq = 0

export function subscriberChannel(topic: string) {
  return supabase.channel(`${topic}::${++subscriberSeq}`)
}

/* ────────────────────────────────────────────────────────────────────────────
 * Samtalekanaler — og hvorfor `subscriberChannel` IKKE kan brukes til dem
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Alt over handler om `postgres_changes`, der emnet bare er et navn klienten
 * finner på. Samtalene fra S1 bruker BROADCAST: en utløser i basen sender til
 * emnet `conversation:<id>`, og utsendingen er EKSAKT emnetreff.
 *
 * Suffikset `::<seq>` — hele poenget med `subscriberChannel` — gjør derfor at
 * en abonnent på `conversation:<id>::3` ALDRI mottar en kringkasting til
 * `conversation:<id>`. Ingen feil, ingen logglinje, bare en samtale der nye
 * meldinger aldri dukker opp. Fable navnga fella i arkitekturgjennomgangen
 * 22.08.2026 før noen hadde skrevet en linje av dette.
 *
 * Så emnet må være delt — og da er clobber-feilen fra toppen av fila tilbake:
 * `removeChannel` fra én komponent melder av alle. Løsningen er ikke å velge
 * mellom de to, det er å telle: ÉN kanal per emne, N lyttere, og avmelding
 * først når den siste går. Det er den ene formen som tåler begge fellene.
 *
 * `private: true` er ikke pynt: `realtime.messages` har RLS med én policy
 * (S0-migreringen), og den avgjør hvem som får bli med i emnet. Uten flagget
 * ber klienten om en offentlig kanal, som policyen aldri ser.
 */
import type { RealtimeChannel } from '@supabase/supabase-js'

export interface ConversationBroadcast {
  /** `REACTION` kommer fra `message_reactions_broadcast()` (S3) og rir på SAMME
      emne som meldingene, så en reaksjon og meldingen den står på ikke kan
      ankomme i feil rekkefølge gjennom to kanaler. */
  operation: 'INSERT' | 'UPDATE' | 'DELETE' | 'MEMBERSHIP' | 'REACTION'
  record: Record<string, unknown> | null
  old_record: Record<string, unknown> | null
  /** Bare på MEMBERSHIP: hvorfor emnet flyttet seg. */
  reason?: string
}

type Listener = (payload: ConversationBroadcast) => void

interface SharedChannel {
  channel: RealtimeChannel
  listeners: Set<Listener>
}

const conversationChannels = new Map<string, SharedChannel>()

/**
 * Abonner på én samtale. Returnerer avmeldingen.
 *
 * Kall den så mange ganger du vil for samme samtale — det blir én kanal, og
 * den lever til den siste lytteren er borte. To komponenter som viser samme
 * tråd (panelet og ulest-telleren) deler altså kanal uten å kunne melde
 * hverandre av.
 *
 * ## `epoch` er ikke et versjonsnummer. Det er hele fjerningsmekanismen (S2).
 *
 * `realtime.messages`-policyen avgjør hvem som får BLI MED i et emne, og den
 * spørres én gang — ved innmelding. Målt i produksjon 22.08.2026 med to ekte
 * nettlesere: etter at `left_at` var satt returnerte den fjernedes REST-lesing
 * **0 rader**, mens den ALLEREDE ÅPNE kanalen hans mottok neste kringkasting og
 * tegnet den. Data lukkes umiddelbart; kontakten gjør det ikke.
 *
 * Derfor bærer emnet `conversations.membership_epoch`, som basen teller opp
 * hver gang et aktivt medlemskap trekkes tilbake. Samtalen flytter seg til et
 * emne den fjernede ikke er med i, og å melde seg inn på nytt kjører policyen
 * om igjen — som nå nekter. Målt: en FRISK innmelding etter fjerning mottar
 * ingenting.
 *
 * De som blir igjen får beskjed om å flytte gjennom en `MEMBERSHIP`-hendelse
 * sendt på DET GAMLE emnet — det siste det emnet noen gang bærer.
 */
export function subscribeToConversation(
  conversationId: string,
  epoch: number,
  listener: Listener
): () => void {
  const topic = `conversation:${conversationId}:${epoch}`
  let shared = conversationChannels.get(topic)

  if (!shared) {
    // Uten dette sendes anon-nøkkelen som identitet, og RLS-policyen på
    // realtime.messages ser en bruker som ikke er noen. Kallet er idempotent.
    void supabase.realtime.setAuth()
    const channel = supabase
      .channel(topic, { config: { private: true } })
      .on('broadcast', { event: '*' }, (msg) => {
        const p = (msg?.payload ?? {}) as Partial<ConversationBroadcast>
        if (!p.operation) return
        const entry = conversationChannels.get(topic)
        // Kopi av settet: en lytter som melder seg av inne i sin egen
        // tilbakekalling skal ikke endre samlingen vi itererer over.
        entry?.listeners.forEach((fn) => fn(p as ConversationBroadcast))
      })
    shared = { channel, listeners: new Set() }
    conversationChannels.set(topic, shared)
    channel.subscribe()
  }

  shared.listeners.add(listener)

  let done = false
  return () => {
    if (done) return          // en avmelding kalt to ganger skal ikke telle to
    done = true
    const entry = conversationChannels.get(topic)
    if (!entry) return
    entry.listeners.delete(listener)
    if (entry.listeners.size === 0) {
      conversationChannels.delete(topic)
      void supabase.removeChannel(entry.channel)
    }
  }
}

/** Bare for tester: hvor mange kanaler står åpne, og med hvor mange lyttere. */
export function __conversationChannelStats() {
  return Array.from(conversationChannels.entries()).map(([topic, s]) => ({
    topic,
    listeners: s.listeners.size,
  }))
}

/* ────────────────────────────────────────────────────────────────────────────
 * Skriveindikatoren — og hvorfor den har SITT EGET emne
 * ────────────────────────────────────────────────────────────────────────────
 *
 * «X skriver …» er den første pakken i dette systemet som kommer fra en KLIENT.
 * Alt annet på et samtaleemne er sendt av en databasetrigger, og `realtime.
 * messages` hadde derfor bare en SELECT-policy: ingen klient fikk sende.
 *
 * ⛔ Den opplagte utvidelsen er feil. Gir vi INSERT på selve samtaleemnet, kan
 *    et hvilket som helst medlem kringkaste `{"operation":"INSERT","record":…}`
 *    — en forfalsket melding, fra et ekte medlem, som ingen klient kan skille
 *    fra en basen sendte. Derfor går skrivetrafikken til et SØSKENEMNE med sin
 *    egen INSERT-policy (`conversation:<id>:<epoch>:typing`), og meldingsemnet
 *    forblir skrive-bare-for-databasen. Det verste en forfalskning kan gjøre på
 *    skriveemnet er å påstå at noen skriver.
 *
 * Epoken rir gratis med: emnet er avledet av meldingsemnet, så en fjernet
 * persons skrivekanal dør sammen med meldingskanalen hans, av samme mekanisme.
 */
export interface TypingPing {
  userId: string
  name: string | null
}

interface TypingChannel {
  channel: RealtimeChannel
  listeners: Set<(p: TypingPing) => void>
}

const typingChannels = new Map<string, TypingChannel>()

/**
 * Abonner på «noen skriver» for én samtale. Returnerer en sender og en
 * avmelding. Ref-tellet på nøyaktig samme måte som meldingskanalen, av samme
 * grunn: to komponenter på samme emne får samme kanalobjekt fra
 * `supabase.channel`, og den enes opprydding ville ellers melde av begge.
 */
export function subscribeToTyping(
  conversationId: string,
  epoch: number,
  listener: (p: TypingPing) => void
): { ping: (me: TypingPing) => void; off: () => void } {
  const topic = `conversation:${conversationId}:${epoch}:typing`
  let shared = typingChannels.get(topic)

  if (!shared) {
    void supabase.realtime.setAuth()
    const channel = supabase
      .channel(topic, { config: { private: true, broadcast: { self: false } } })
      .on('broadcast', { event: 'typing' }, (msg) => {
        const p = (msg?.payload ?? {}) as Partial<TypingPing>
        if (!p.userId) return
        typingChannels.get(topic)?.listeners.forEach((fn) => fn(p as TypingPing))
      })
    shared = { channel, listeners: new Set() }
    typingChannels.set(topic, shared)
    channel.subscribe()
  }

  shared.listeners.add(listener)
  const entry = shared

  let done = false
  return {
    ping: (me) => {
      // Feiler den, feiler den stille. En skriveindikator som kaster en feil
      // inn i skrivefeltet ville vært verre enn ingen skriveindikator.
      void entry.channel.send({ type: 'broadcast', event: 'typing', payload: me }).catch(() => {})
    },
    off: () => {
      if (done) return
      done = true
      const e = typingChannels.get(topic)
      if (!e) return
      e.listeners.delete(listener)
      if (e.listeners.size === 0) {
        typingChannels.delete(topic)
        void supabase.removeChannel(e.channel)
      }
    },
  }
}

/** Bare for tester. */
export function __typingChannelStats() {
  return Array.from(typingChannels.entries()).map(([topic, s]) => ({
    topic,
    listeners: s.listeners.size,
  }))
}
